import React, {
	useEffect,
	useMemo,
	useState,
	useCallback,
	useRef,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useSelector, useDispatch } from 'react-redux';
import { ServerSideDataSource } from '../../apollo';
import { getColumns } from './columnsDefinitions';
import DefinitionDrawer, {
	DefinitionContext,
} from '../../components/Definition/index';
import { getChartType } from '../../components/Definition';
import { CUSTOM_ATTR, ERROR_SCREEN_MSG_STORE } from '../../constants/constant';
import { generateColumnDefs } from './generateColumnsDefs';
import { timePeriodOptions } from '../Filter/TableFilters';
import { colors } from '../../../src/theme/color-palette.js';
import { reloadShowHideCol } from '../../redux/actions/salesActions';
import {
	getUserPreferenceMetrics,
	mappingKeys,
	formatCellValue,
	getValueForExcel,
	addExpandedGroupId,
	removeExpandedGroupId,
	hasExpandedGroupId,
	isOtherExpanded,
} from './utils';
import FullWidthCellRenderer from './FullWidthCellRenderer';
import CustomTableCell from './CustomTableCell.js';
import {
	setExcelDownloadAction,
	setEnableDownload,
} from '../../redux/actions/tableActions';
import analytics from '../../helper/analytics';
import tracker from '../../constants/tracker';
import { useHistory } from 'react-router-dom';

/**
 * Custom Overlay Text to show when No data is available on the Table
 */
const CustomNoRowsOverlay = () => {
	return `Table ${ERROR_SCREEN_MSG_STORE}`;
};

const Table = () => {
	const [firstTimeLoadingTable, setFirstTimeLoadingTable] = useState(false);
	const dispatch = useDispatch();
	const navigate = useHistory();
	// Load table filters from Redux
	const tableFiltersStates = useSelector((state) => state.tableReducer);
	const viewBy = tableFiltersStates.viewByFilter;
	const breakdownBy = tableFiltersStates.breakDownByFilter;
	const timePeriod = tableFiltersStates.newTimePeriodFilter;
	const { readyToDownload, viewByType, breakDownByType } = useSelector(
		(state) => state.tableReducer
	);
	const rootParam = [];

	const gridRef = useRef();
	const [columnDefs, setColumnDefs] = useState([]);
	const [itemInfoColumnVisibility, setItemInfoColumnVisibility] =
		useState(true);
	const [expandedRows, setExpandedRows] = useState([]);

	const toggleItemInfoSec = (state) => {
		const itemInfoSection = ['upcNbr', 'wmtItemNo', 'catalogItemId', 'primeItemNbr', 'allLinkNbr'];
		gridRef.current?.columnApi?.setColumnsVisible(itemInfoSection, state);
	};

	const hierarchyFilterRhid = useSelector(
		(state) =>
			state.fullCompanyViewReducer?.selectedHierarchy?.hierarchyFilterRhId
	);

	const onRowGroupOpened = (params) => {
		const groupId = params.data?.id;
		const roots = params.data.rootParam ? params.data.rootParam : null;
		const viewByOfType = params.data.type;
		const viewByLevel = params.data.level;

		if (breakdownBy === 'ITEM' || breakdownBy === 'ALL_LINK_NUMBER' || breakdownBy === 'PRIME_ITEM_NUMBER') {
			setItemInfoColumnVisibility(true);
			setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
		} else if (viewByOfType != CUSTOM_ATTR) {
			switch (viewByLevel) {
				case 'SUBCATEGORY':
					if (breakdownBy !== 'BRAND' && selectedFilter !== 'Store') {
						setItemInfoColumnVisibility(true);
						setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					}
					break;
				case 'BRAND':
					if (breakdownBy === '' || breakdownBy === 'BRAND') {
						setItemInfoColumnVisibility(true);
						setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					}
					break;
				case 'STORE':
					if(!(breakdownBy === 'DEPARTMENT' || breakdownBy === 'CATEGORY' || breakdownBy === 'SUBCATEGORY' || breakdownBy === 'BRAND'  || breakdownBy === 'FINELINE')) {
						setItemInfoColumnVisibility(() => true);
						setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					}
					break;
				case 'ALL_LINK_NUMBER':
					setItemInfoColumnVisibility(true);
					setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					break;
				case 'PRIME_ITEM_NUMBER':
					setItemInfoColumnVisibility(true);
					setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					break;
				case 'FINELINE':
					if(!(breakdownBy === 'BRAND' || breakdownBy === 'STATE' || breakdownBy === 'REGION' || breakdownBy === 'MARKET'  || breakdownBy === 'SUB_DIVISION')) {
						setItemInfoColumnVisibility(() => true);
						setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
					}
					break;
			}
		} else if (breakdownBy === '') {
			setItemInfoColumnVisibility(true);
			setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
		} else if (roots) {
			setItemInfoColumnVisibility(true);
			setExpandedRows(addExpandedGroupId(groupId, expandedRows, roots));
		}

		if (params.node.expanded === false) {
			if (hasExpandedGroupId(groupId, expandedRows, roots)) {
				setExpandedRows(removeExpandedGroupId(groupId, expandedRows, roots));
			}
		}
	};

	useEffect(() => {
		// If no other groups are expanded, Then we collapse the item info section.
		if (viewBy === 'ITEM' || viewBy === 'ALL_LINK_NUMBER' || viewBy === 'PRIME_ITEM_NUMBER') {
			setItemInfoColumnVisibility(true);
		} else {
			if (!isOtherExpanded(expandedRows)) {
				setItemInfoColumnVisibility(false);
			}
		}
	}, [expandedRows]);

	const { columnNames, userPreferenceList } = useSelector(
		(state) => state.columnReducer
	);

	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);

	const isUPC = useSelector((state) => state.appGuideReducer.isUPC);

	const sessionWeekFilters = JSON.parse(sessionStorage.getItem('weekfilters'));

	const { reloadTable, selectedFilters, selectedUPC } = useSelector(
		(state) => state.salesReducer
	);

	const { weekFilter, startDate, endDate } =
		sessionWeekFilters && Object.keys(sessionWeekFilters)?.length != 0
			? sessionWeekFilters
			: selectedFilters;

	const { selectedFilter } = selectedFilters;

	const userDetails = JSON.parse(localStorage.getItem('PORTAL_CONTEXT'));

	const initialCxt = {
		isDrawerOpened: false,
		metricType: '',
	};
	const [definition, setDefinition] = useState(initialCxt);
	// let expandedRows = [];

	// Grid Options
	const gridOptions = {
		// Passing this AGgrid api to use in DataSource in order to set the Top pinned Total row in Table
		api: gridRef.current?.api,
		// To disable the menu coming on Table right click
		suppressContextMenu: true,

		// Disable no rows overlay
		suppressNoRowsOverlay: true,

		// Open drilldown page in new tab, for Items
		onCellClicked: (e) => {
			if (e?.data?.level === 'ITEM' && e.colDef.field === 'description') {
				const itemNameParam = e?.data?.name === '' ? '' : e?.data?.name;
				const itemNameParams = itemNameParam.replace('%', 'pct');

				navigate.push({
					pathname: `/details/item-details/${e?.data?.id}/${itemNameParams}`,
				});
				analytics.trackEventInfo({
					type: tracker.EVENTS.CLICK,
					actionOn: tracker.ACTION_ON.ITEM_DRILLDOWN,
				});
			}
		},
	};

	// GraphQL Query Params
	const metrics = getUserPreferenceMetrics(
		userPreferenceList[selectedFilter.toUpperCase()]
	);
	let graphQlParams = {
		viewBy,
		metrics,
		endDate,
		startDate,
		weekFilter,
		breakdownBy,
		channel: selectedFilter,
		[isUPC ? 'upcs' : 'items']: selectedUPC?.length > 0 ? selectedUPC : null,
		timePeriods: timePeriod?.length > 0 ? timePeriod : null,
		rhids: hierarchyFilterRhid ? hierarchyFilterRhid.split(',') : null,
		rootParam,
		userDetails,
		selectedView,
		viewByType,
		breakDownByType,
	};

	// Creating a new dataSource object based on the new graphQL params
	const dataSource = new ServerSideDataSource(gridOptions, graphQlParams);

	// Wait for the Hierrachy IDs To be set
	useEffect(() => {
		// Setting the dataSource to our AG Grid instance when it's the first time.
		if (firstTimeLoadingTable === false && hierarchyFilterRhid?.length > 0) {
			gridRef.current?.api?.setServerSideDatasource(dataSource);
			setFirstTimeLoadingTable(true);
		}
	}, [hierarchyFilterRhid]);

	const itemColumnDef = [
		{
			headerName: 'Item Info',
			hide: true,
			children: [
				{
					field: '',
					headerName: '',
					sortable: false,
					children: [
						{
							field: 'upcNbr',
							headerName: 'UPC',
							type: 'leftAligned',
							maxWidth: 150,
							sortable: false,
						},
						{
							field: 'wmtItemNo',
							headerName: 'WMT Item Number',
							type: 'leftAligned',
							maxWidth: 150,
							sortable: false,
						},
						{
							field: 'catalogItemId',
							headerName: 'Catalog Item Id',
							type: 'leftAligned',
							maxWidth: 150,
							sortable: false,
						},
						{
							field: 'primeItemNbr',
							headerName: 'Prime Item Number',
							type: 'leftAligned',
							maxWidth: 150,
							sortable: false,
						},
						{
							field: 'allLinkNbr',
							headerName: 'All Links Number',
							type: 'leftAligned',
							maxWidth: 150,
							sortable: false,
						},
					],
				},
			],
		},
	];

	const cxtValue = React.useMemo(
		() => ({ definition, setDefinition }),
		[definition]
	);

	useEffect(() => {
		// Set columns definition based on the selectedFilter, If not set,use Omni
		setColumnDefs(
			generateColumnDefs(
				timePeriod,
				[
					...itemColumnDef,
					...getColumns(selectedFilter || 'Omni', setDefinition, getChartType),
				],
				weekFilter,
				timePeriodOptions,
				selectedFilter
			)
		);
	}, [selectedFilter]);

	// Monitor changes for time period comparison selection, To create new columns for specific time period
	useEffect(() => {
		gridRef.current?.api?.setColumnDefs(
			generateColumnDefs(
				timePeriod,
				columnDefs,
				weekFilter,
				timePeriodOptions,
				selectedFilter
			)
		);
	}, [timePeriod, weekFilter, startDate, endDate, columnDefs, selectedView]);

	// To trigger the GraphQL calls when there is any change in the params
	useEffect(() => {
		// Get the metrics selected by the User from Custom Table Columns
		const metrics = getUserPreferenceMetrics(
			userPreferenceList[selectedFilter.toUpperCase()]
		);

		if (viewBy && hierarchyFilterRhid?.length > 0 && firstTimeLoadingTable) {
			graphQlParams = {
				viewBy,
				endDate,
				metrics,
				startDate,
				weekFilter,
				breakdownBy,
				channel: selectedFilter,
				[isUPC ? 'upcs' : 'items']:
					selectedUPC?.length > 0 ? selectedUPC : null,
				timePeriods: timePeriod?.length > 0 ? timePeriod : null,
				rhids: hierarchyFilterRhid ? hierarchyFilterRhid.split(',') : null,
				rootParam,
				userDetails,
				selectedView,
				viewByType,
				breakDownByType,
			};

			// Update GraphQL Query Params
			dataSource?.setQueryParams(graphQlParams);
			gridRef.current?.api?.setServerSideDatasource(dataSource);

			// Refresh the data on AG Grid
			//gridRef.current?.api?.refreshServerSide({ purge: true });

			// Reset expanded rows
			setExpandedRows([]);
		}
	}, [
		viewBy,
		endDate,
		startDate,
		weekFilter,
		timePeriod,
		breakdownBy,
		selectedUPC,
		selectedFilter,
		userPreferenceList,
		hierarchyFilterRhid,
		selectedView,
	]);

	// use to re-render the table column api to get the correct columns
	useEffect(() => {
		if (reloadTable) {
			dispatch(reloadShowHideCol(false));
		} else {
			dispatch(reloadShowHideCol(true));
		}
	}, [selectedFilter]);

	// To trigger the export to excel(download) from Ag grid
	useEffect(() => {
		if (readyToDownload) {
			const downloadTime = new Date().toLocaleString()?.replace(',', ' ');
			gridRef.current?.api?.exportDataAsExcel({
				fileName: `Scintilla-Channel Performance-${downloadTime}`,
				sheetName: 'Data',
				shouldRowBeSkipped(params) {
					return params.node.data?.fullWidth;
				},
				processCellCallback(params) {
					return params.column.colDef.colId === 'ag-Grid-AutoColumn'
						? params?.node?.data?.description
						: getValueForExcel(params);
				},
			});
			// set the store value to default value
			dispatch(setExcelDownloadAction(false));
		}
	}, [readyToDownload]);

	const onGridReady = () => {
		if (hierarchyFilterRhid?.length > 0) {
			gridRef.current?.api?.setServerSideDatasource(dataSource);
		}
	};

	useEffect(() => {
		// using ag grid columnApi to get the list of current displayed columns so that we can have a check
		const displayedCol = gridRef.current?.columnApi?.getAllGridColumns() || [];
		const currentCol = displayedCol?.map((e) => e.colId);

		// current selection of show columns based on table preference
		let tablePreferenceList = [];
		if (selectedFilter === 'Omni') {
			if (columnNames['OMNI'].length > 0) {
				tablePreferenceList = columnNames['OMNI'];
			} else {
				// Fallback if the OMNI column preferences is not available in saved user pref.
				tablePreferenceList = currentCol;
			}
		} else if (selectedFilter === 'eCom') {
			if (columnNames['ECOM'].length > 0) {
				tablePreferenceList = columnNames['ECOM'];
			} else {
				// Fallback if the ECOM column preferences is not available in saved user pref.
				tablePreferenceList = currentCol;
			}
		} else {
			if (columnNames['STORE'].length > 0) {
				tablePreferenceList = columnNames['STORE'];
			} else {
				// Fallback if the STORE column preferences is not available in saved user pref.
				tablePreferenceList = currentCol;
			}
		}

		// mapped the current show table preference with the field names
		let mappedTablePreference = [];
		for (let val of tablePreferenceList) {
			mappedTablePreference.push(mappingKeys[val]);
		}

		// Prepare show and hide columns list so that can pass to gri api
		const showColumns = [];
		currentCol?.map((e) => {
			for (let key of mappedTablePreference) {
				if (e.includes(key)) {
					showColumns.push(e);
				}
			}
		});

		const hideColumns = currentCol.filter((val) => !showColumns.includes(val));

		// Check if there are visible columns, then hide the unwanted columns.
		if (showColumns.length > 0) {
			gridRef.current?.columnApi?.setColumnsVisible(hideColumns, false);
		}

		showColumns.unshift(
			'ag-Grid-AutoColumn',
			'upcNbr',
			'wmtItemNo',
			'catalogItemId',
			'primeItemNbr',
			'allLinkNbr',
		);
		// pass the list visible columns list to grid api
		gridRef.current?.columnApi?.setColumnsVisible(showColumns, true);
	}, [selectedFilter, columnNames, weekFilter, timePeriod, reloadTable]);

	const noRowsOverlayComponent = useMemo(() => {
		return CustomNoRowsOverlay;
	}, []);

	useEffect(() => {
		toggleItemInfoSec(itemInfoColumnVisibility);
	}),
		[itemInfoColumnVisibility];

	const customColorFormatter = (cell) => {
		let style = {};
		// Discussions https://github.com/ag-grid/ag-grid/issues/6928
		// cell.column is not a documented property!!
		if (
			cell?.column?.colId === cell?.column?.parent?.displayedChildren[0]?.colId
		) {
			style.borderLeft = `0.5px solid ${colors.LIGHT_GREY}`;
		}

		// if (cell?.value < 0) {
		// 	style.background = colors.ERROR_LIGHT_HIGHLIGHT;
		// 	style.color = colors.ERROR_MD;
		// 	// style.borderRadius = '4px';
		// 	// style.padding = '4px';
		// }

		return style;
	};

	const getValue = (value, colDef) => {
		if (value == null || value == undefined) {
			return '-';
		}

		if (
			['UPC', 'WMT Item Number', 'Catalog Item Id', 'Prime Item Number', 'All Links Number'].includes(colDef.headerName)
		) {
			return value;
		}

		if (value === -999999) return 'NA';

		return formatCellValue(value, colDef?.field);
	};

	// To enable setting for all columns use this otherwise use the column defination on individual column
	const defaultColDef = useMemo(() => {
		return {
			suppressMenu: true, // remove the column menu
			minWidth: 150,
			flex: 1,
			cellStyle: (cell) => {
				return customColorFormatter(cell);
			},
			valueFormatter: (params) => {
				return getValue(params.value, params.colDef);
			},
			sortable: true,
			sortingOrder: ['desc', 'asc'],
			cellRenderer: CustomTableCell,
			type: 'rightAligned',
		};
	}, []);

	const autoGroupColumnDef = useMemo(() => {
		return {
			headerName: '',
			field: 'description',
			minWidth: 260,
			sortable: false,
			resizable: true,
			pinned: 'left',
			enableRowGroup: true,
			cellClass: (params) =>
				params?.data?.level === 'ITEM'
					? ['custom-item-cell', 'nameFormat']
					: 'nameFormat',
			cellRendererParams: {
				innerRenderer: (params) => {
					return params.data.description || null;
				},
			},
		};
	}, []);

	const isServerSideGroupOpenByDefault = useCallback((params) => {
		// keep  all groups closed by default
		return params.rowNode.level < 0;
	}, []);
	const isServerSideGroup = useCallback((dataItem) => {
		// indicate if node is a group
		return dataItem?.group;
	}, []);
	const getServerSideGroupKey = useCallback((dataItem) => {
		// specify which group key to use
		return dataItem?.id;
	}, []);

	const rowClassRules = {
		leafRow: function (params) {
			return !params?.data?.group ? true : false;
		},
		hideErrorRows: function (params) {
			return params?.node?.failedLoad;
		},
		tableMessage: function (params) {
			return params?.node?.data?.fullWidth;
		},
	};

	const isFullWidthRow = useCallback((params) => {
		return params.rowNode.data?.fullWidth;
	}, []);

	const fullWidthCellRenderer = useMemo(() => {
		return FullWidthCellRenderer;
	}, []);

	const getRowHeight = (params) => {
		return params?.data?.fullWidth ? 24 : 54;
	};

	const onRowDataUpdated = () => {
		dispatch(setEnableDownload(true));
	};

	const excelStyles = useMemo(() => {
		return [
			{
				id: 'nameFormat',
				alignment: {
					horizontal: 'Left',
				},
			},
			{
				id: 'floatFormat',
				numberFormat: {
					format: '#,##0.00',
				},
				alignment: {
					horizontal: 'Right',
				},
			},
			{
				id: 'unitsFormat',
				numberFormat: {
					format: '#,##0',
				},
				alignment: {
					horizontal: 'Right',
				},
			},
			{
				id: 'currencyFormat',
				numberFormat: {
					format: '$#,##0.00',
				},
				alignment: {
					horizontal: 'Right',
				},
			},
			{
				id: 'percentFormat',
				numberFormat: {
					format: '#,##0.00%',
				},
				alignment: {
					horizontal: 'Right',
				},
			},
		];
	});

	return (
		<div>
			<div
				className="ag-theme-alpine ag-theme-scintilla"
				style={{
					width: '100%',
					height: '735px',
					minHeight: '300px',
					marginBottom: '56px',
					borderRadius: '8px',
				}}>
				<AgGridReact
					onGridReady={onGridReady}
					data-test={'table-component'}
					ref={gridRef}
					onRowDataUpdated={onRowDataUpdated}
					columnDefs={columnDefs}
					rowClassRules={rowClassRules}
					suppressCellFocus={true}
					defaultColDef={defaultColDef}
					autoGroupColumnDef={autoGroupColumnDef}
					rowModelType={'serverSide'}
					treeData={true}
					animateRows={true}
					scrollbarWidth={5} // width for horizontal scrollbar
					isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
					isServerSideGroup={isServerSideGroup}
					getServerSideGroupKey={getServerSideGroupKey}
					gridOptions={gridOptions}
					noRowsOverlayComponent={noRowsOverlayComponent}
					suppressMovableColumns={true}
					suppressMultiSort={true}
					isFullWidthRow={isFullWidthRow}
					fullWidthCellRenderer={fullWidthCellRenderer}
					getRowHeight={getRowHeight}
					excelStyles={excelStyles}
					enableCellTextSelection={true}
					onRowGroupOpened={onRowGroupOpened}
				/>
			</div>
			<DefinitionContext.Provider value={cxtValue}>
				<DefinitionDrawer
					isOpen={definition.isDrawerOpened}
					metricType={definition.metricType}
					onClose={() => setDefinition(initialCxt)}
				/>
			</DefinitionContext.Provider>
		</div>
	);
};

export default React.memo(Table);
